/**
 * Mock document service — backed by the in-memory `mockDocuments` fixtures.
 *
 * Mirrors the customers mock service: returns copies, simulates latency, and
 * never mutates the shared fixture array (the store owns React state).
 */

import { mockDocuments } from '@/data/mockDocuments'
import { mockDelay } from '@/services/factory'
import type { DocumentService } from './documents.service'
import type {
  Document,
  DocumentVersion,
  DocumentListParams,
  UploadDocumentInput,
  DocumentEntityType,
} from '@/types/document.types'

function applyParams(docs: Document[], params?: DocumentListParams): Document[] {
  let out = [...docs]
  if (params?.status) out = out.filter((d) => d.status === params.status)
  if (params?.category) out = out.filter((d) => d.category === params.category)
  if (params?.fileType) out = out.filter((d) => d.fileType === params.fileType)
  if (params?.linkedEntityId) {
    out = out.filter((d) => d.linkedEntityId === params.linkedEntityId)
  }
  return out
}

export function createMockDocumentService(): DocumentService {
  return {
    async list(params?: DocumentListParams): Promise<Document[]> {
      await mockDelay(300)
      return applyParams(mockDocuments, params)
    },

    async listForEntity(
      _entityType: DocumentEntityType,
      entityId: string
    ): Promise<Document[]> {
      await mockDelay(300)
      return mockDocuments.filter((d) => d.linkedEntityId === entityId)
    },

    async get(id: string): Promise<Document> {
      await mockDelay(200)
      const doc = mockDocuments.find((d) => d.id === id)
      if (!doc) throw new Error('Document not found')
      return { ...doc }
    },

    async upload(input: UploadDocumentInput): Promise<Document> {
      await mockDelay(600)
      const now = new Date().toISOString()
      const name =
        input.name ?? input.file?.name ?? `${input.category}-${Date.now()}.pdf`
      const fileType = name.includes('.')
        ? name.split('.').pop()!.toLowerCase()
        : 'pdf'
      const fileSize = input.file?.size ?? 0
      const newDoc: Document = {
        id: `doc-${Date.now()}`,
        name,
        description: input.description,
        filePath: `/tenant/documents/${name}`,
        fileSize,
        fileType,
        mimeType: input.file?.type || 'application/pdf',
        fileSizeMb: Math.round((fileSize / (1024 * 1024)) * 100) / 100,
        category: input.category,
        assessmentYear: input.assessmentYear,
        financialYear: input.financialYear,
        workspaceService: input.workspaceService,
        status: 'uploaded',
        accessLevel: 'team',
        tags: [],
        linkedEntityType: input.linkedEntityType,
        linkedEntityId: input.linkedEntityId,
        parentDocumentId: input.parentDocumentId,
        isVerified: false,
        version: input.version ?? 1,
        isLatest: true,
        createdAt: now,
        updatedAt: now,
      }
      mockDocuments.unshift(newDoc)
      return newDoc
    },

    async remove(_id: string, _reason?: string): Promise<void> {
      await mockDelay(300)
    },

    async verify(
      _id: string,
      isVerified: boolean,
      notes?: string
    ): Promise<Partial<Document>> {
      await mockDelay(300)
      return {
        isVerified,
        status: isVerified ? 'verified' : 'uploaded',
        verificationNotes: notes,
        verifiedAt: isVerified ? new Date().toISOString() : undefined,
        updatedAt: new Date().toISOString(),
      }
    },

    async search(q: string): Promise<Document[]> {
      await mockDelay(300)
      const query = q.toLowerCase().trim()
      if (query.length < 2) return []
      return mockDocuments.filter(
        (d) =>
          d.name.toLowerCase().includes(query) ||
          (d.description?.toLowerCase().includes(query) ?? false)
      )
    },

    async versions(id: string): Promise<DocumentVersion[]> {
      await mockDelay(200)
      const doc = mockDocuments.find((d) => d.id === id)
      if (!doc) return []
      return [
        {
          id: doc.id,
          version: doc.version,
          isLatest: doc.isLatest,
          parentDocumentId: doc.parentDocumentId,
          fileHash: doc.fileHash,
          fileSize: doc.fileSize,
          createdByName: doc.createdByName,
          createdAt: doc.createdAt,
        },
      ]
    },

    async getDownloadUrl(id: string): Promise<string> {
      await mockDelay(200)
      return `https://storage.taxhive.local/download/${id}`
    },

    async bulkDelete(_ids: string[]): Promise<void> {
      await mockDelay(500)
    },

    async bulkVerify(_ids: string[]): Promise<void> {
      await mockDelay(500)
    },
  }
}
